import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, CreditCard, Search, ChevronDown, ChevronRight, ArrowUpDown, Download, Calendar } from 'lucide-react';

// ─── Types ──────────────────────────────────────────────────────────────────

interface CCAccount {
  id: string;
  name: string;
  balance: number;
}

interface CCTransaction {
  id: string;
  date: string;
  vendor: string;
  amount: number;
  credit: boolean;
  accountName: string;
  accountId: string;
  category: string;
  description: string;
}

interface CompanyCC {
  realmId: string;
  companyName: string;
  accounts: CCAccount[];
  transactions: CCTransaction[];
}

interface DateRange {
  startDate: string;
  endDate: string;
}

// ─── Constants ──────────────────────────────────────────────────────────────

const API_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:3001') + '/api';

const COMPANY_COLORS: Record<string, string> = {
  'GGA ProShop LLC': 'bg-blue-100 text-blue-800',
  'Graves Golf Custom Clubs LLC': 'bg-purple-100 text-purple-800',
  'Graves Golf Instruction LLC': 'bg-green-100 text-green-800',
};

const fmt = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(n);

const fmtDate = (d: string) => {
  const date = new Date(d + 'T00:00:00');
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// ─── Component ──────────────────────────────────────────────────────────────

type SortField = 'date' | 'vendor' | 'amount' | 'category' | 'accountName';
type SortDir = 'asc' | 'desc';

const CreditCardUsage: React.FC = () => {
  const [companies, setCompanies] = useState<CompanyCC[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const currentYear = new Date().getFullYear();
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: `${currentYear}-01-01`,
    endDate: new Date().toISOString().split('T')[0],
  });
  const [customDateRange, setCustomDateRange] = useState<DateRange>({ ...dateRange });
  const [showCustom, setShowCustom] = useState(false);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCompany, setSelectedCompany] = useState<string>('all');
  const [selectedAccount, setSelectedAccount] = useState<string>('all');

  // Sorting
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  // Expanded vendor groups
  const [expandedVendors, setExpandedVendors] = useState<Set<string>>(new Set());

  // View mode
  const [viewMode, setViewMode] = useState<'transactions' | 'by-vendor' | 'by-category'>('transactions');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      });
      const response = await fetch(`${API_BASE}/quickbooks/cc-transactions?${params}`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const json = await response.json();
      if (!json.success) throw new Error(json.error || 'Failed to load data');
      setCompanies(json.data.companies);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load credit card data');
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => { loadData(); }, [loadData]);

  // Date range presets
  const setPreset = (key: string) => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    let start: string, end: string;

    switch (key) {
      case 'thisMonth':
        start = `${y}-${String(m + 1).padStart(2, '0')}-01`;
        end = now.toISOString().split('T')[0];
        break;
      case 'lastMonth': {
        const lm = m === 0 ? 11 : m - 1;
        const ly = m === 0 ? y - 1 : y;
        const lastDay = new Date(ly, lm + 1, 0).getDate();
        start = `${ly}-${String(lm + 1).padStart(2, '0')}-01`;
        end = `${ly}-${String(lm + 1).padStart(2, '0')}-${lastDay}`;
        break;
      }
      case 'thisQuarter': {
        const qStart = Math.floor(m / 3) * 3;
        start = `${y}-${String(qStart + 1).padStart(2, '0')}-01`;
        end = now.toISOString().split('T')[0];
        break;
      }
      case 'thisYear':
        start = `${y}-01-01`;
        end = now.toISOString().split('T')[0];
        break;
      case 'lastYear':
        start = `${y - 1}-01-01`;
        end = `${y - 1}-12-31`;
        break;
      default:
        return;
    }
    setDateRange({ startDate: start, endDate: end });
    setCustomDateRange({ startDate: start, endDate: end });
    setShowCustom(false);
  };

  const applyCustomRange = () => {
    setDateRange(customDateRange);
    setShowCustom(false);
  };

  const formatDateLabel = () => {
    const s = new Date(dateRange.startDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const e = new Date(dateRange.endDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${s} - ${e}`;
  };

  // Flatten all transactions with company info
  const allTransactions = companies.flatMap(c =>
    c.transactions.map(t => ({ ...t, companyName: c.companyName }))
  );

  // All CC accounts across companies
  const allAccounts = companies.flatMap(c =>
    c.accounts.map(a => ({ ...a, companyName: c.companyName }))
  );

  // Filter
  const filtered = allTransactions.filter(t => {
    if (selectedCompany !== 'all' && t.companyName !== selectedCompany) return false;
    if (selectedAccount !== 'all' && t.accountId !== selectedAccount) return false;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      return (t.vendor || '').toLowerCase().includes(q)
        || (t.category || '').toLowerCase().includes(q)
        || (t.description || '').toLowerCase().includes(q);
    }
    return true;
  });

  // Sort
  const sorted = [...filtered].sort((a, b) => {
    let cmp = 0;
    switch (sortField) {
      case 'date': cmp = a.date.localeCompare(b.date); break;
      case 'vendor': cmp = (a.vendor || '').localeCompare(b.vendor || ''); break;
      case 'amount': cmp = a.amount - b.amount; break;
      case 'category': cmp = (a.category || '').localeCompare(b.category || ''); break;
      case 'accountName': cmp = (a.accountName || '').localeCompare(b.accountName || ''); break;
    }
    return sortDir === 'desc' ? -cmp : cmp;
  });

  // Aggregations
  const totalSpend = filtered.filter(t => !t.credit).reduce((s, t) => s + t.amount, 0);
  const totalCredits = filtered.filter(t => t.credit).reduce((s, t) => s + t.amount, 0);
  const netSpend = totalSpend - totalCredits;

  // By-vendor grouping
  const vendorGroups = filtered.reduce<Record<string, typeof filtered>>((acc, t) => {
    const key = t.vendor || 'Unknown';
    if (!acc[key]) acc[key] = [];
    acc[key].push(t);
    return acc;
  }, {});
  const vendorSummary = Object.entries(vendorGroups)
    .map(([vendor, txns]) => ({
      vendor,
      count: txns.length,
      total: txns.filter(t => !t.credit).reduce((s, t) => s + t.amount, 0),
      credits: txns.filter(t => t.credit).reduce((s, t) => s + t.amount, 0),
      transactions: txns,
    }))
    .sort((a, b) => b.total - a.total);

  // By-category grouping
  const categoryGroups = filtered.reduce<Record<string, typeof filtered>>((acc, t) => {
    const key = t.category || 'Uncategorized';
    if (!acc[key]) acc[key] = [];
    acc[key].push(t);
    return acc;
  }, {});
  const categorySummary = Object.entries(categoryGroups)
    .map(([category, txns]) => ({
      category,
      count: txns.length,
      total: txns.filter(t => !t.credit).reduce((s, t) => s + t.amount, 0),
    }))
    .sort((a, b) => b.total - a.total);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const SortHeader = ({ field, label }: { field: SortField; label: string }) => (
    <th
      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none"
      onClick={() => handleSort(field)}
    >
      <span className="flex items-center gap-1">
        {label}
        {sortField === field && (
          <ArrowUpDown className="h-3 w-3 text-blue-500" />
        )}
      </span>
    </th>
  );

  const exportCSV = () => {
    const headers = ['Date', 'Company', 'Card', 'Vendor', 'Category', 'Amount', 'Credit', 'Description'];
    const rows = sorted.map(t => [
      t.date,
      t.companyName,
      t.accountName,
      t.vendor,
      t.category,
      t.amount.toFixed(2),
      t.credit ? 'Yes' : 'No',
      `"${(t.description || '').replace(/"/g, '""')}"`,
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cc-transactions-${dateRange.startDate}-to-${dateRange.endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-3 text-gray-600">Loading credit card data...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <p className="text-red-700">{error}</p>
        <button onClick={loadData} className="mt-3 text-sm text-red-600 underline hover:text-red-800">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Credit Card Usage</h1>
          <p className="text-gray-600">{formatDateLabel()}</p>
        </div>
      </div>

      {/* Date Range Picker */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <Calendar className="h-4 w-4 text-gray-500" />
          {[
            { key: 'thisMonth', label: 'This Month' },
            { key: 'lastMonth', label: 'Last Month' },
            { key: 'thisQuarter', label: 'This Quarter' },
            { key: 'thisYear', label: 'This Year' },
            { key: 'lastYear', label: 'Last Year' },
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setPreset(key)}
              className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors ${
                !showCustom && dateRange.startDate === customDateRange.startDate
                  ? 'bg-gray-100 text-gray-700'
                  : 'text-gray-500 hover:bg-gray-50'
              }`}
            >
              {label}
            </button>
          ))}
          <button
            onClick={() => setShowCustom(!showCustom)}
            className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors ${
              showCustom ? 'bg-blue-50 text-blue-700' : 'text-gray-500 hover:bg-gray-50'
            }`}
          >
            Custom
          </button>
          {showCustom && (
            <div className="flex items-center gap-2 ml-2">
              <input
                type="date"
                value={customDateRange.startDate}
                onChange={(e) => setCustomDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                className="px-2 py-1 text-sm border border-gray-300 rounded-md"
              />
              <span className="text-gray-400">to</span>
              <input
                type="date"
                value={customDateRange.endDate}
                onChange={(e) => setCustomDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                className="px-2 py-1 text-sm border border-gray-300 rounded-md"
              />
              <button
                onClick={applyCustomRange}
                className="px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Apply
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Account Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {allAccounts.map((acct) => (
          <div
            key={`${acct.companyName}-${acct.id}`}
            className={`bg-white rounded-lg border p-4 cursor-pointer transition-all ${
              selectedAccount === acct.id ? 'border-blue-500 ring-2 ring-blue-100' : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => setSelectedAccount(selectedAccount === acct.id ? 'all' : acct.id)}
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <CreditCard className="h-4 w-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-900">{acct.name}</span>
                </div>
                <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full ${COMPANY_COLORS[acct.companyName] || 'bg-gray-100 text-gray-700'}`}>
                  {acct.companyName}
                </span>
              </div>
              <div className="text-right">
                <p className={`text-lg font-semibold ${acct.balance < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                  {fmt(Math.abs(acct.balance))}
                </p>
                <p className="text-xs text-gray-500">balance</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Total Charges</p>
          <p className="text-xl font-semibold text-gray-900">{fmt(totalSpend)}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Credits/Returns</p>
          <p className="text-xl font-semibold text-green-600">{fmt(totalCredits)}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Net Spend</p>
          <p className="text-xl font-semibold text-gray-900">{fmt(netSpend)}</p>
        </div>
      </div>

      {/* Filters & View Toggle */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex gap-3 items-center flex-wrap">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search vendor, category..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 w-64"
            />
          </div>
          <select
            value={selectedCompany}
            onChange={(e) => setSelectedCompany(e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Companies</option>
            {companies.map(c => (
              <option key={c.realmId} value={c.companyName}>{c.companyName}</option>
            ))}
          </select>
        </div>
        <div className="flex gap-2 items-center">
          {(['transactions', 'by-vendor', 'by-category'] as const).map(mode => (
            <button
              key={mode}
              onClick={() => setViewMode(mode)}
              className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                viewMode === mode
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {mode === 'transactions' ? 'All' : mode === 'by-vendor' ? 'By Vendor' : 'By Category'}
            </button>
          ))}
          <button
            onClick={exportCSV}
            className="ml-2 px-3 py-1.5 text-sm bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 flex items-center gap-1"
          >
            <Download className="h-3.5 w-3.5" />
            CSV
          </button>
        </div>
      </div>

      {/* Transaction List View */}
      {viewMode === 'transactions' && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <SortHeader field="date" label="Date" />
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company</th>
                  <SortHeader field="accountName" label="Card" />
                  <SortHeader field="vendor" label="Vendor" />
                  <SortHeader field="category" label="Category" />
                  <SortHeader field="amount" label="Amount" />
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {sorted.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                      No transactions found for this period.
                    </td>
                  </tr>
                ) : (
                  sorted.map((t, i) => (
                    <tr key={`${t.id}-${i}`} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{fmtDate(t.date)}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${COMPANY_COLORS[t.companyName] || 'bg-gray-100 text-gray-700'}`}>
                          {t.companyName.replace(' LLC', '')}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{t.accountName.replace('Credit Cards Payable:', '')}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 font-medium">{t.vendor}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {t.category.split(':').pop()?.trim() || t.category}
                      </td>
                      <td className={`px-4 py-3 text-sm font-medium whitespace-nowrap text-right ${t.credit ? 'text-green-600' : 'text-gray-900'}`}>
                        {t.credit ? '+' : ''}{fmt(t.amount)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {sorted.length > 0 && (
            <div className="bg-gray-50 px-4 py-3 border-t border-gray-200 text-sm text-gray-500">
              Showing {sorted.length} transaction{sorted.length !== 1 ? 's' : ''}
            </div>
          )}
        </div>
      )}

      {/* By Vendor View */}
      {viewMode === 'by-vendor' && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vendor</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Transactions</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {vendorSummary.map(({ vendor, count, total, transactions: txns }) => {
                const expanded = expandedVendors.has(vendor);
                return (
                  <React.Fragment key={vendor}>
                    <tr
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => {
                        setExpandedVendors(prev => {
                          const next = new Set(prev);
                          if (next.has(vendor)) next.delete(vendor);
                          else next.add(vendor);
                          return next;
                        });
                      }}
                    >
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        <span className="flex items-center gap-2">
                          {expanded ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
                          {vendor}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 text-right">{count}</td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right">{fmt(total)}</td>
                    </tr>
                    {expanded && txns.map((t, i) => (
                      <tr key={`${t.id}-${i}`} className="bg-gray-50/50">
                        <td className="pl-12 pr-4 py-2 text-sm text-gray-600">
                          {fmtDate(t.date)} — {t.category.split(':').pop()?.trim()}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-500 text-right">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${COMPANY_COLORS[t.companyName] || 'bg-gray-100 text-gray-700'}`}>
                            {t.companyName.replace(' LLC', '')}
                          </span>
                        </td>
                        <td className={`px-4 py-2 text-sm font-medium text-right ${t.credit ? 'text-green-600' : 'text-gray-700'}`}>
                          {t.credit ? '+' : ''}{fmt(t.amount)}
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* By Category View */}
      {viewMode === 'by-category' && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Transactions</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">% of Spend</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {categorySummary.map(({ category, count, total }) => (
                <tr key={category} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">
                    {category.split(':').pop()?.trim() || category}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 text-right">{count}</td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right">{fmt(total)}</td>
                  <td className="px-4 py-3 text-sm text-gray-600 text-right">
                    {totalSpend > 0 ? `${((total / totalSpend) * 100).toFixed(1)}%` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
            {categorySummary.length > 0 && (
              <tfoot className="bg-gray-50">
                <tr className="font-medium">
                  <td className="px-4 py-3 text-sm text-gray-900">Total</td>
                  <td className="px-4 py-3 text-sm text-gray-600 text-right">
                    {categorySummary.reduce((s, c) => s + c.count, 0)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 text-right">{fmt(totalSpend)}</td>
                  <td className="px-4 py-3 text-sm text-gray-600 text-right">100%</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
};

export default CreditCardUsage;
